/**
 * UTM Tracking Utility
 * Captures UTM parameters from URL and stores them for later use during sign up
 */

const UTM_STORAGE_KEY = 'utm_attribution';

/**
 * Extract UTM parameters from URL query string
 * @param {Object} query - Vue router query object or URLSearchParams
 * @returns {Object} Object containing UTM parameters
 */
export function extractUtmParams(query) {
  const utmParams = {};
  
  // Standard UTM parameters
  const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
  
  utmKeys.forEach(key => {
    if (query[key]) {
      utmParams[key] = query[key];
    }
  });
  
  // Google Ads specific parameters (legacy)
  if (query['gclid']) {
    utmParams.gclid = query['gclid'];
  }
  
  // Google Ads Enhanced Conversions parameters (new)
  const gadKeys = ['gad_source', 'gad_campaignid', 'gad_adsetid', 'gad_adid', 'gad_keywordid', 'gad_creativeid', 'gad_placementid'];
  
  gadKeys.forEach(key => {
    if (query[key]) {
      utmParams[key] = query[key];
    }
  });
  
  return Object.keys(utmParams).length > 0 ? utmParams : null;
}

/**
 * Store UTM parameters in localStorage
 * @param {Object} utmParams - UTM parameters object
 */
export function storeUtmParams(utmParams) {
  if (!utmParams || Object.keys(utmParams).length === 0) {
    return;
  }
  
  // Store with timestamp for expiration logic if needed
  const data = {
    params: utmParams,
    timestamp: Date.now(),
  };
  
  window.app.storage.setItem(UTM_STORAGE_KEY, data);
}

/**
 * Get stored UTM parameters from localStorage
 * @returns {Object|null} Stored UTM parameters or null
 */
export function getStoredUtmParams() {
  const stored = window.app.storage.getItem(UTM_STORAGE_KEY);
  
  if (!stored || !stored.params) {
    return null;
  }
  
  // Optional: Add expiration logic (e.g., 30 days)
  const EXPIRATION_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
  if (Date.now() - stored.timestamp > EXPIRATION_MS) {
    window.app.storage.removeItem(UTM_STORAGE_KEY);
    return null;
  }
  
  return stored.params;
}

/**
 * Clear stored UTM parameters
 */
export function clearUtmParams() {
  window.app.storage.removeItem(UTM_STORAGE_KEY);
}

/**
 * Capture UTM parameters from current URL and store them
 * @param {Object} route - Vue router route object
 */
export function captureUtmFromRoute(route) {
  const utmParams = extractUtmParams(route.query);
  
  if (utmParams) {
    storeUtmParams(utmParams);
  }
}
